// DevGitPanel.js — Git status, checkpoints, diff (phase 4)

import React, { useState, useEffect, useCallback, memo } from 'react';
import {
    GitBranch, GitCommit, ChevronDown, ChevronUp, RotateCcw, Upload,
    Loader2, FileDiff, RefreshCw,
} from 'lucide-react';
import DevDiffView from './DevDiffView';
import DevGitConfirmModal from './DevGitConfirmModal';
import {
    getGitStatus, listCheckpoints, createCheckpoint, restoreCheckpoint, pushToGitHub,
    getGitDiff, DevCockpitApiError,
} from '../../services/devCockpitApi';
import './devCockpit.css';

function formatDate(iso) {
    try {
        return new Date(iso).toLocaleString('fr-FR', {
            day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
        });
    } catch {
        return iso;
    }
}

function DevGitPanel({
    workspaceReady,
    selectedPath,
    onRefreshWorkspace,
}) {
    const [open, setOpen] = useState(false);
    const [tab, setTab] = useState('checkpoints');
    const [gitStatus, setGitStatus] = useState(null);
    const [checkpoints, setCheckpoints] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [diffText, setDiffText] = useState('');
    const [diffLoading, setDiffLoading] = useState(false);
    const [confirm, setConfirm] = useState(null);
    const [actionLoading, setActionLoading] = useState(false);

    const load = useCallback(async () => {
        if (!workspaceReady) return;
        setLoading(true);
        setError(null);
        try {
            const [st, cps] = await Promise.all([getGitStatus(), listCheckpoints()]);
            setGitStatus(st.status);
            setCheckpoints(cps.checkpoints || []);
        } catch (e) {
            setError(e instanceof DevCockpitApiError ? e.message : 'Erreur git');
        } finally {
            setLoading(false);
        }
    }, [workspaceReady]);

    useEffect(() => {
        load();
    }, [load]);

    const loadDiffSnapshot = useCallback(async () => {
        if (!workspaceReady || !selectedPath) {
            setDiffText('');
            return;
        }
        setDiffLoading(true);
        try {
            const data = await getGitDiff(selectedPath);
            setDiffText(data.has_changes ? data.diff : '');
        } catch {
            setDiffText('');
        } finally {
            setDiffLoading(false);
        }
    }, [workspaceReady, selectedPath]);

    useEffect(() => {
        if (open && tab === 'diff') {
            loadDiffSnapshot();
        }
    }, [open, tab, loadDiffSnapshot]);

    const handleCommit = () => {
        setConfirm({
            type: 'commit',
            title: 'Créer un checkpoint',
            description: 'Committer tous les changements du sandbox en un snapshot git local.',
            detail: `${gitStatus?.changed_count || 0} fichier(s) modifié(s)`,
            confirmLabel: 'Committer',
            danger: false,
            onConfirm: async () => {
                setActionLoading(true);
                try {
                    const msg = `checkpoint: ${new Date().toISOString().slice(0, 16).replace('T', ' ')}`;
                    await createCheckpoint(msg);
                    setConfirm(null);
                    await load();
                    onRefreshWorkspace?.();
                } catch (e) {
                    setError(e instanceof DevCockpitApiError ? e.message : 'Échec commit');
                } finally {
                    setActionLoading(false);
                }
            },
        });
    };

    const handleRestore = (cp) => {
        setConfirm({
            type: 'restore',
            title: 'Restaurer ce checkpoint',
            description: 'ATTENTION : git reset --hard — toutes les modifications non commitées seront perdues.',
            detail: `${cp.git_sha_short} — ${cp.message}`,
            confirmLabel: 'Restaurer',
            danger: true,
            onConfirm: async () => {
                setActionLoading(true);
                try {
                    await restoreCheckpoint(cp.id);
                    setConfirm(null);
                    await load();
                    onRefreshWorkspace?.();
                } catch (e) {
                    setError(e instanceof DevCockpitApiError ? e.message : 'Échec restore');
                } finally {
                    setActionLoading(false);
                }
            },
        });
    };

    const handlePush = () => {
        setConfirm({
            type: 'push',
            title: 'Push vers GitHub',
            description: 'Envoyer les commits locaux vers le dépôt distant. Action irréversible côté remote.',
            detail: `origin → ${gitStatus?.branch || 'main'}`,
            confirmLabel: 'Push',
            danger: true,
            onConfirm: async () => {
                setActionLoading(true);
                try {
                    await pushToGitHub();
                    setConfirm(null);
                    await load();
                } catch (e) {
                    setError(e instanceof DevCockpitApiError ? e.message : 'Échec push');
                } finally {
                    setActionLoading(false);
                }
            },
        });
    };

    if (!workspaceReady) return null;

    const branch = gitStatus?.branch || '…';
    const dirtyCount = gitStatus?.changed_count || 0;

    return (
        <div className={`dev-git-panel${open ? ' dev-git-panel--open' : ''}`}>
            <button
                type="button"
                className="dev-git-panel__toggle"
                onClick={() => setOpen((v) => !v)}
            >
                <GitBranch size={12} />
                <span className="dev-git-panel__branch">{branch}</span>
                {dirtyCount > 0 && (
                    <span className="dev-git-panel__dirty">{dirtyCount} modif.</span>
                )}
                {gitStatus?.head && (
                    <span className="dev-git-panel__head">{gitStatus.head}</span>
                )}
                <span style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center' }}>
                    {loading && <Loader2 size={11} style={{ animation: 'spin 1s linear infinite' }} />}
                    {open ? <ChevronDown size={12} /> : <ChevronUp size={12} />}
                </span>
            </button>

            {open && (
                <div className="dev-git-panel__body">
                    {error && <div className="dev-git-panel__error">{error}</div>}

                    <div className="dev-git-panel__toolbar">
                        <button
                            type="button"
                            className="dev-btn dev-btn--primary"
                            onClick={handleCommit}
                            disabled={!gitStatus?.dirty || actionLoading}
                        >
                            <GitCommit size={12} />
                            Checkpoint
                        </button>
                        {gitStatus?.push_enabled && (
                            <button
                                type="button"
                                className="dev-btn"
                                onClick={handlePush}
                                disabled={actionLoading}
                            >
                                <Upload size={12} />
                                Push GitHub
                            </button>
                        )}
                        <button type="button" className="dev-btn dev-btn--icon" onClick={load} title="Rafraîchir">
                            <RefreshCw size={12} />
                        </button>
                    </div>

                    <div className="dev-git-panel__tabs">
                        <button
                            type="button"
                            className={`dev-git-panel__tab${tab === 'diff' ? ' dev-git-panel__tab--active' : ''}`}
                            onClick={() => setTab('diff')}
                        >
                            <FileDiff size={11} />
                            Diff
                        </button>
                        <button
                            type="button"
                            className={`dev-git-panel__tab${tab === 'checkpoints' ? ' dev-git-panel__tab--active' : ''}`}
                            onClick={() => setTab('checkpoints')}
                        >
                            <GitCommit size={11} />
                            Checkpoints ({checkpoints.length})
                        </button>
                    </div>

                    <div className="dev-git-panel__content">
                        {tab === 'diff' && (
                            <>
                                <div className="dev-git-panel__diff-actions">
                                    <button
                                        type="button"
                                        className="dev-btn"
                                        onClick={loadDiffSnapshot}
                                    >
                                        <RefreshCw size={11} />
                                        Actualiser diff
                                    </button>
                                </div>
                                <DevDiffView
                                    path={selectedPath}
                                    diff={diffText}
                                    loading={diffLoading}
                                />
                            </>
                        )}
                        {tab === 'checkpoints' && (
                            <div className="dev-git-checkpoints">
                                {checkpoints.length === 0 && (
                                    <div className="dev-git-diff__empty">Aucun checkpoint — modifiez puis committez</div>
                                )}
                                {checkpoints.map((cp) => (
                                    <div key={cp.id} className="dev-git-checkpoint">
                                        <div className="dev-git-checkpoint__main">
                                            <span className="dev-git-checkpoint__sha">{cp.git_sha_short}</span>
                                            <span className="dev-git-checkpoint__msg">{cp.message}</span>
                                            <span className="dev-git-checkpoint__date">{formatDate(cp.created_at)}</span>
                                        </div>
                                        <button
                                            type="button"
                                            className="dev-btn dev-btn--icon"
                                            onClick={() => handleRestore(cp)}
                                            title="Restaurer"
                                            disabled={actionLoading}
                                        >
                                            <RotateCcw size={11} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {confirm && (
                <DevGitConfirmModal
                    {...confirm}
                    onCancel={() => !actionLoading && setConfirm(null)}
                    loading={actionLoading}
                />
            )}
        </div>
    );
}

export default memo(DevGitPanel);
